import React, { useMemo, useState, useEffect, useCallback, useRef } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./PetHome.css";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { addItem } from "../redux/action";
import { sendRequest } from "../ApiRequest";
import { sendCachedRequest } from "../utils/apiCache";
import { BASE_URL } from "../URL/constants";
import useHtmlLinkNavigation from "../hooks/useHtmlLinkNavigation";

const formatCurrency = (value = 0) => {
  const amount = Number(value) || 0;
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
  }).format(amount);
};

// Capitalize first letter of each word in category name
const capitalizeCategoryName = (categoryName) => {
  if (!categoryName) return "";
  return categoryName
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

const PetHome = ({ 
  carouselHtml = [],
  tagline = "",
  companyName = "Pet Shop",
  bodyHtml = [],
  branches: initialBranches = []
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const bodyContentRef = useHtmlLinkNavigation(bodyHtml);

  const [petProducts, setPetProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsError, setProductsError] = useState(null);
  const [productCategories, setProductCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [categoriesError, setCategoriesError] = useState(null);
  const [activeProductCategory, setActiveProductCategory] = useState(null);
  const [productsByCategory, setProductsByCategory] = useState({});
  const [allProducts, setAllProducts] = useState([]);
  
  // Slider ref for custom navigation
  const sliderRef = useRef(null);
  
  // Booking form states
  const [bookingFormOpen, setBookingFormOpen] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingMessage, setBookingMessage] = useState(null);
  const [selectedServiceIds, setSelectedServiceIds] = useState([]);
  const [petServices, setPetServices] = useState([]);
  
  // Category popup states
  const [categoryPopupOpen, setCategoryPopupOpen] = useState(false);
  
  const todayDate = useMemo(() => new Date().toISOString().split("T")[0], []);
  const [bookingData, setBookingData] = useState({
    name: "",
    phone: "",
    email: "",
    city: "",
    branchId: "",
    date: todayDate,
    time: "10:00",
    notes: ""
  });

  // Carousel settings
  const settings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 4000,
    arrows: true,
    fade: true,
  };

  // Track product index for unique images per category
  const productIndexByCategory = React.useRef({});

  // Get default image based on category and product index
  const getDefaultImageByCategory = (categoryName, productIndex = 0) => {
    const category = (categoryName || "").toLowerCase();
    
    // Different images for different categories
    const categoryImages = {
      "pet food": [
        "https://images.unsplash.com/photo-1601758228041-f3b2795255f1?w=500&h=500&q=80", // Dog food
        "https://images.unsplash.com/photo-1601758228041-f3b2795255f1?w=500&h=500&q=80", // Cat food
        "https://images.unsplash.com/photo-1601758228041-f3b2795255f1?w=500&h=500&q=80", // Pet treats
        "https://images.unsplash.com/photo-1601758228041-f3b2795255f1?w=500&h=500&q=80", // Puppy food
        "https://images.unsplash.com/photo-1601758228041-f3b2795255f1?w=500&h=500&q=80"  // Premium food
      ],
      "pet accessories": [
        "https://images.unsplash.com/photo-1601758228041-f3b2795255f1?w=500&h=500&q=80", // Collar
        "https://images.unsplash.com/photo-1601758228041-f3b2795255f1?w=500&h=500&q=80", // Leash
        "https://images.unsplash.com/photo-1601758228041-f3b2795255f1?w=500&h=500&q=80", // Bowl
        "https://images.unsplash.com/photo-1601758228041-f3b2795255f1?w=500&h=500&q=80", // Harness
        "https://images.unsplash.com/photo-1601758228041-f3b2795255f1?w=500&h=500&q=80"  // Accessories
      ],
      "pet toys": [
        "https://images.unsplash.com/photo-1601758228041-f3b2795255f1?w=500&h=500&q=80", // Ball
        "https://images.unsplash.com/photo-1601758228041-f3b2795255f1?w=500&h=500&q=80", // Interactive toy
        "https://images.unsplash.com/photo-1601758228041-f3b2795255f1?w=500&h=500&q=80", // Rope toy
        "https://images.unsplash.com/photo-1601758228041-f3b2795255f1?w=500&h=500&q=80", // Scratching post
        "https://images.unsplash.com/photo-1601758228041-f3b2795255f1?w=500&h=500&q=80"  // Squeaky toy
      ],
      "pet care": [
        "https://images.unsplash.com/photo-1601758228041-f3b2795255f1?w=500&h=500&q=80", // Shampoo
        "https://images.unsplash.com/photo-1601758228041-f3b2795255f1?w=500&h=500&q=80", // Brush
        "https://images.unsplash.com/photo-1601758228041-f3b2795255f1?w=500&h=500&q=80", // Nail clipper
        "https://images.unsplash.com/photo-1601758228041-f3b2795255f1?w=500&h=500&q=80", // Dental kit
        "https://images.unsplash.com/photo-1601758228041-f3b2795255f1?w=500&h=500&q=80"  // Grooming wipes
      ],
      "pet services": [
        "https://images.unsplash.com/photo-1601758228041-f3b2795255f1?w=500&h=500&q=80", // Boarding
        "https://images.unsplash.com/photo-1601758228041-f3b2795255f1?w=500&h=500&q=80", // Grooming
        "https://images.unsplash.com/photo-1601758228041-f3b2795255f1?w=500&h=500&q=80", // Spa
        "https://images.unsplash.com/photo-1601758228041-f3b2795255f1?w=500&h=500&q=80", // Training
        "https://images.unsplash.com/photo-1601758228041-f3b2795255f1?w=500&h=500&q=80"  // Daycare
      ]
    };

    // Find matching category
    for (const [key, images] of Object.entries(categoryImages)) {
      if (category.includes(key)) {
        // Use product index to cycle through images
        const index = productIndex % images.length;
        return images[index];
      }
    }

    // Default fallback
    return "https://images.unsplash.com/photo-1601758228041-f3b2795255f1?w=500&h=500&q=80";
  };

  const mapPetProductData = useCallback(
    (item, index = 0) => {
      const categoryName = item.category_name || item.category?.name || "Pet Products";
      
      // Initialize category index if not exists
      if (!productIndexByCategory.current[categoryName]) {
        productIndexByCategory.current[categoryName] = 0;
      }
      
      // Get product image or use category-based default
      let productImage = item.item_images?.[0] || item.image_url || item.image;
      
      // If no image provided, use category-based default with cycling index
      if (!productImage) {
        const categoryIndex = productIndexByCategory.current[categoryName];
        productImage = getDefaultImageByCategory(categoryName, categoryIndex);
        // Increment index for next product in same category
        productIndexByCategory.current[categoryName] = (categoryIndex + 1) % 5;
      }

      return {
        id: item.id,
        item_name: item.item_name || item.name,
        sales_price: item.sales_price || item.selling_price || 0,
        sales_discount: item.sales_discount || 0,
        mrp: item.mrp || item.original_price || item.sales_price || 0,
        image: productImage,
        category: categoryName,
        quantity: Number(item.quantity) || 0,
        description: item.short_description || item.description || "",
        is_service: item.quantity === -1,
        stock_quantity: Number(item.quantity) || 0,
      };
    },
    []
  );

  const fetchPetProducts = useCallback(
    async (categoryId = null) => {
      try {
        setProductsLoading(true);
        setProductsError(null);
        
        const response = await sendCachedRequest(
          "GET",
          `${BASE_URL}/v1/public_site/menus/item_lists`,
          null,
          {
            params: {
              per_page: 200,
              ...(categoryId ? { category_id: categoryId } : {})
            },
            cacheKey: categoryId ? `item_lists_pet_products_${categoryId}` : "item_lists_pet_products_all"
          }
        );

        const items = response?.data?.items || [];

        // Check if this is Pet Services category - if yes, show only -1 quantity items
        const category = productCategories.find(cat => String(cat.id) === String(categoryId));
        const categoryName = category?.name?.toLowerCase() || '';
        const isPetServicesCategory = categoryId && (
          categoryName.includes('pet services') || 
          categoryName.includes('services') ||
          categoryName === 'pet services'
        );

        // Filter items based on category
        const filtered = items
          .filter((item) => {
            const quantity = Number(item?.quantity);
            // For Pet Services category: show only -1 quantity items
            if (isPetServicesCategory) {
              return quantity === -1;
            }
            // For other categories: show items with quantity -1 OR quantity > 0
            return quantity === -1 || quantity > 0;
          })
          .map((item, index) => mapPetProductData(item, index));

        const dataset = filtered.length > 0 ? filtered : items.map((item) => mapPetProductData(item));

        if (categoryId) {
          setProductsByCategory((prev) => ({
            ...prev,
            [categoryId]: dataset
          }));
        }

        setPetProducts(dataset);
        
        // Store all products if no category filter
        if (!categoryId) {
          setAllProducts(dataset);
        }
      } catch (fetchError) {
        console.error("Failed to load pet products:", fetchError);
        setProductsError("Unable to load pet products right now.");
        setPetProducts([]);
      } finally {
        setProductsLoading(false);
      }
    },
    [mapPetProductData]
  );

  const fetchProductCategories = useCallback(async () => {
    try {
      setCategoriesLoading(true);
      setCategoriesError(null);
      const response = await sendCachedRequest(
        "GET",
        `${BASE_URL}/v1/public_site/menus/fetch_categories`,
        null,
        {
          cacheKey: "pet_product_categories"
        }
      );

      const fetchedCategories = Array.isArray(response?.data?.categories)
        ? response.data.categories
        : [];

      // Verify each category has items by fetching items for that category (in parallel)
      const categoryVerificationPromises = fetchedCategories.map(async (category) => {
        const categoryId = String(category?.id);
        if (!categoryId) return null;

        try {
          // Fetch items for this category to verify it has items
          const itemsResponse = await sendCachedRequest(
            "GET",
            `${BASE_URL}/v1/public_site/menus/item_lists`,
            null,
            {
              params: {
                category_id: categoryId,
                per_page: 1 // Only need to check if at least one item exists
              },
              cacheKey: `category_verify_pet_${categoryId}`
            }
          );

          const items = itemsResponse?.data?.items || [];
          
          // Check if this is Pet Services category
          const categoryName = category?.name?.toLowerCase() || '';
          const isPetServicesCategory = categoryName.includes('pet services') || categoryName.includes('services');
          
          // Check if category has valid items
          if (items.length > 0) {
            const hasValidItems = items.some((item) => {
              const quantity = Number(item?.quantity);
              // For Pet Services: only -1 quantity
              if (isPetServicesCategory) {
                return quantity === -1;
              }
              // For others: -1 OR > 0
              return quantity === -1 || quantity > 0;
            });

            if (hasValidItems) {
              return category;
            }
          }
          return null;
        } catch (error) {
          console.warn(`Failed to verify items for category ${categoryId}:`, error);
          return null;
        }
      });

      // Wait for all verifications to complete
      const verifiedCategories = await Promise.all(categoryVerificationPromises);
      const categoriesWithItems = verifiedCategories.filter((cat) => cat !== null);

      setProductCategories(categoriesWithItems);

      // Default to "All Products" (null)
      setActiveProductCategory(null);
      fetchPetProducts(null);
    } catch (catError) {
      console.error("Failed to fetch product categories:", catError);
      setProductCategories([]);
      setCategoriesError("Unable to load product categories right now.");
      setActiveProductCategory(null);
      fetchPetProducts();
    } finally {
      setCategoriesLoading(false);
    }
  }, [fetchPetProducts]);

  const handleCategoryChange = useCallback(
    (categoryId) => {
      if (categoryId === activeProductCategory) return;
      setActiveProductCategory(categoryId);
      setCategoryPopupOpen(false); // Close popup when category is selected
      const productsSection = document.querySelector(".pet-products-section");
      if (productsSection) {
        productsSection.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    },
    [activeProductCategory]
  );

  // Open category popup
  const openCategoryPopup = useCallback(() => {
    setCategoryPopupOpen(true);
  }, []);

  // Close category popup
  const closeCategoryPopup = useCallback(() => {
    setCategoryPopupOpen(false);
  }, []);

  const activeCategoryLabel = useMemo(() => {
    if (!activeProductCategory) return "";
    const match = productCategories.find(
      (category) => String(category.id) === String(activeProductCategory)
    );
    return capitalizeCategoryName(match?.name || "");
  }, [activeProductCategory, productCategories]);

  // Fetch pet services (only -1 quantity items) for booking form
  const fetchPetServices = useCallback(async () => {
    try {
      const response = await sendCachedRequest(
        "GET",
        `${BASE_URL}/v1/public_site/menus/item_lists`,
        null,
        {
          params: {
            per_page: 200
          },
          cacheKey: "pet_services_all"
        }
      );

      const items = response?.data?.items || [];
      
      // Filter only -1 quantity items (services)
      const services = items
        .filter((item) => Number(item?.quantity) === -1)
        .map((item) => mapPetProductData(item));

      setPetServices(services);
    } catch (error) {
      console.error("Failed to load pet services:", error);
      setPetServices([]);
    }
  }, [mapPetProductData]);

  useEffect(() => {
    fetchProductCategories();
  }, [fetchProductCategories]);

  // Listen for header button click
  useEffect(() => {
    const handleOpenBooking = () => {
      fetchPetServices();
      setSelectedServiceIds([]);
      setBookingFormOpen(true);
      setBookingMessage(null);
    };
    
    window.addEventListener('open-pet-booking-form', handleOpenBooking);
    
    return () => {
      window.removeEventListener('open-pet-booking-form', handleOpenBooking);
    };
  }, [fetchPetServices]);

  // Listen for Services link click - show all services
  useEffect(() => {
    const handleShowServices = async () => {
      try {
        setProductsLoading(true);
        setProductsError(null);
        
        // Fetch all items
        const response = await sendCachedRequest(
          "GET",
          `${BASE_URL}/v1/public_site/menus/item_lists`,
          null,
          {
            params: {
              per_page: 200
            },
            cacheKey: "item_lists_pet_services_all"
          }
        );

        const items = response?.data?.items || [];
        
        // Filter only services (quantity === -1)
        const services = items
          .filter((item) => Number(item?.quantity) === -1)
          .map((item, index) => mapPetProductData(item, index));

        setPetProducts(services);
        setActiveProductCategory('services'); // Set a special marker for services
        
        // Scroll to products section
        setTimeout(() => {
          const productsSection = document.querySelector(".pet-products-section");
          if (productsSection) {
            productsSection.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }, 100);
      } catch (error) {
        console.error("Failed to load services:", error);
        setProductsError("Unable to load services right now.");
        setPetProducts([]);
      } finally {
        setProductsLoading(false);
      }
    };
    
    window.addEventListener('show-pet-services', handleShowServices);
    
    return () => {
      window.removeEventListener('show-pet-services', handleShowServices);
    };
  }, [mapPetProductData]);

  useEffect(() => {
    if (activeProductCategory === null) {
      // Show all products
      if (allProducts.length > 0) {
        setPetProducts(allProducts);
      } else {
        fetchPetProducts(null);
      }
      return;
    }
    
    if (activeProductCategory) {
      if (productsByCategory[activeProductCategory]) {
        setPetProducts(productsByCategory[activeProductCategory]);
        return;
      }
      fetchPetProducts(activeProductCategory);
    }
  }, [activeProductCategory, fetchPetProducts, productsByCategory, allProducts]);

  // Add drag-to-scroll functionality for category filter
  useEffect(() => {
    const categoryFilter = document.querySelector('.pet-categories-filter.pet-categories-scrollable, .pet-categories-filter.pet-categories-slider');
    if (!categoryFilter) return;

    let isDown = false;
    let startX;
    let scrollLeft;
    let hasMoved = false;

    const handleMouseDown = (e) => {
      // Don't start drag if clicking on a button
      if (e.target.closest('.pet-category-btn')) {
        return;
      }
      isDown = true;
      hasMoved = false;
      categoryFilter.style.cursor = 'grabbing';
      startX = e.pageX - categoryFilter.offsetLeft;
      scrollLeft = categoryFilter.scrollLeft;
    };

    const handleMouseLeave = () => {
      isDown = false;
      categoryFilter.style.cursor = 'grab';
      hasMoved = false;
    };

    const handleMouseUp = (e) => {
      if (isDown && hasMoved) {
        e.preventDefault();
      }
      isDown = false;
      categoryFilter.style.cursor = 'grab';
      hasMoved = false;
    };

    const handleMouseMove = (e) => {
      if (!isDown) return;
      const x = e.pageX - categoryFilter.offsetLeft;
      const walk = (x - startX) * 2;
      
      // Only prevent default and scroll if movement is significant
      if (Math.abs(walk) > 5) {
        hasMoved = true;
        e.preventDefault();
        categoryFilter.scrollLeft = scrollLeft - walk;
      }
    };

    // Touch events for mobile
    let touchStartX;
    let touchScrollLeft;
    let touchHasMoved = false;

    const handleTouchStart = (e) => {
      if (e.target.closest('.pet-category-btn')) {
        return;
      }
      touchStartX = e.touches[0].pageX - categoryFilter.offsetLeft;
      touchScrollLeft = categoryFilter.scrollLeft;
      touchHasMoved = false;
    };

    const handleTouchMove = (e) => {
      if (!touchStartX) return;
      const x = e.touches[0].pageX - categoryFilter.offsetLeft;
      const walk = (x - touchStartX) * 2;
      
      if (Math.abs(walk) > 5) {
        touchHasMoved = true;
        categoryFilter.scrollLeft = touchScrollLeft - walk;
      }
    };

    const handleTouchEnd = (e) => {
      if (touchHasMoved && e.target.closest('.pet-category-btn')) {
        e.preventDefault();
      }
      touchStartX = null;
      touchHasMoved = false;
    };

    categoryFilter.addEventListener('mousedown', handleMouseDown);
    categoryFilter.addEventListener('mouseleave', handleMouseLeave);
    categoryFilter.addEventListener('mouseup', handleMouseUp);
    categoryFilter.addEventListener('mousemove', handleMouseMove);
    categoryFilter.addEventListener('touchstart', handleTouchStart, { passive: true });
    categoryFilter.addEventListener('touchmove', handleTouchMove, { passive: false });
    categoryFilter.addEventListener('touchend', handleTouchEnd);

    return () => {
      categoryFilter.removeEventListener('mousedown', handleMouseDown);
      categoryFilter.removeEventListener('mouseleave', handleMouseLeave);
      categoryFilter.removeEventListener('mouseup', handleMouseUp);
      categoryFilter.removeEventListener('mousemove', handleMouseMove);
      categoryFilter.removeEventListener('touchstart', handleTouchStart);
      categoryFilter.removeEventListener('touchmove', handleTouchMove);
      categoryFilter.removeEventListener('touchend', handleTouchEnd);
    };
  }, [productCategories.length]);

  // Open booking form
  const openBookingForm = useCallback((serviceId = null) => {
    fetchPetServices();
    if (serviceId) {
      setSelectedServiceIds([Number(serviceId)]);
    } else {
      setSelectedServiceIds([]);
    }
    setBookingFormOpen(true);
    setBookingMessage(null);
  }, [fetchPetServices]);

  const handleAddToCart = useCallback(async (product) => {
    try {
      const currentUser = JSON.parse(localStorage.getItem("userDetails"));
      let cartState = JSON.parse(localStorage.getItem("cartState")) || {};
      
      // Check if already in cart
      const isInCart = !!cartState[product.id];
      if (isInCart) {
        console.log("Item already in cart");
        return;
      }

      // If not logged in, add to guest cart
      if (!currentUser || !currentUser.data || !currentUser.data.token || !currentUser.data.customer) {
        const guestCart = JSON.parse(localStorage.getItem("guestCart")) || {};
        const cartItemId = `guest_${Date.now()}_${product.id}`;

        const cartItem = {
          id: cartItemId,
          item_id: product.id,
          item_name: product.item_name,
          sales_price: product.sales_price,
          sales_discount: product.sales_discount || 0,
          img: product.image,
          quantity: 1
        };

        guestCart[cartItemId] = cartItem;
        localStorage.setItem("guestCart", JSON.stringify(guestCart));
        
        // Dispatch Redux action for guest cart
        dispatch(addItem(cartItem));
        
        // Update cart state
        cartState[product.id] = true;
        localStorage.setItem("cartState", JSON.stringify(cartState));
        
        // Dispatch custom event for header cart badge update
        window.dispatchEvent(new Event('cart-updated'));
        
        console.log("Item added to guest cart:", cartItem);
        return;
      }

      // If logged in, add via API
      const token = currentUser.data.token;
      const customerId = currentUser.data.customer.id;

      const cartData = {
        customer_id: customerId,
        item_id: product.id,
        quantity: 1
      };

      const response = await sendRequest(
        'POST',
        `${BASE_URL}/v1/public_site/customers/${customerId}/cart/cart_items`,
        cartData,
        { Authorization: token }
      );
      
      // Dispatch Redux action for logged-in user cart
      if (response?.data) {
        const cartItem = {
          id: response.data.id || product.id,
          item_id: product.id,
          item_name: product.item_name,
          sales_price: product.sales_price,
          sales_discount: product.sales_discount || 0,
          img: product.image,
          quantity: 1
        };
        dispatch(addItem(cartItem));
        
        // Update cart state
        cartState[product.id] = true;
        localStorage.setItem("cartState", JSON.stringify(cartState));
        
        // Dispatch custom event for header cart badge update
        window.dispatchEvent(new Event('cart-updated'));
        
        console.log("Item added to cart via API:", cartItem);
      } else {
        console.error("Error adding to cart: No response data");
      }
    } catch (error) {
      console.error('Error adding item to cart:', error);
      
      // Fallback: add to Redux state even if API fails
      const currentUser = JSON.parse(localStorage.getItem("userDetails"));
      let cartState = JSON.parse(localStorage.getItem("cartState")) || {};
      
      if (!currentUser || !currentUser.data || !currentUser.data.token || !currentUser.data.customer) {
        // Guest user fallback
        const guestCart = JSON.parse(localStorage.getItem("guestCart")) || {};
        const cartItemId = `guest_${Date.now()}_${product.id}`;
        
        const cartItem = {
          id: cartItemId,
          item_id: product.id,
          item_name: product.item_name,
          sales_price: product.sales_price,
          sales_discount: product.sales_discount || 0,
          img: product.image,
          quantity: 1
        };
        
        guestCart[cartItemId] = cartItem;
        localStorage.setItem("guestCart", JSON.stringify(guestCart));
        dispatch(addItem(cartItem));
      } else {
        // Logged in user fallback
        const cartItem = {
          id: product.id,
          item_id: product.id,
          item_name: product.item_name,
          sales_price: product.sales_price,
          sales_discount: product.sales_discount || 0,
          img: product.image,
          quantity: 1
        };
        dispatch(addItem(cartItem));
      }
      
      cartState[product.id] = true;
      localStorage.setItem("cartState", JSON.stringify(cartState));
      window.dispatchEvent(new Event('cart-updated'));
    }
  }, [dispatch]);

  // Close booking form
  const closeBookingForm = useCallback(() => {
    setBookingFormOpen(false);
    setSelectedServiceIds([]);
    setBookingMessage(null);
    setBookingData((prev) => ({
      ...prev,
      name: "",
      phone: "",
      email: "",
      notes: ""
    }));
  }, []);

  // Handle booking field changes
  const handleBookingFieldChange = useCallback((e) => {
    const { name, value } = e.target;
    setBookingData((prev) => ({
      ...prev,
      [name]: value
    }));
  }, []);

  // Handle phone input
  const handlePhoneInput = useCallback((e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
    setBookingData((prev) => ({
      ...prev,
      phone: value
    }));
  }, []);

  // Toggle service selection
  const toggleServiceSelection = useCallback((serviceId) => {
    const normalizedId = Number(serviceId);
    setSelectedServiceIds((prev) => {
      if (prev.includes(normalizedId)) {
        return prev.filter((id) => id !== normalizedId);
      }
      return [...prev, normalizedId];
    });
  }, []);

  // Handle booking submit
  const handleBookingSubmit = useCallback(async (event) => {
    event.preventDefault();
    if (!bookingData.name.trim()) {
      setBookingMessage({ type: "error", text: "Please enter your name." });
      return;
    }
    const phone = bookingData.phone.trim();
    if (!/^[6-9]\d{9}$/.test(phone)) {
      setBookingMessage({ type: "error", text: "Please enter a valid 10-digit Indian phone number (starting with 6, 7, 8, or 9)." });
      return;
    }
    if (selectedServiceIds.length === 0) {
      setBookingMessage({ type: "error", text: "Please select at least one service." });
      return;
    }

    setBookingLoading(true);
    setBookingMessage(null);

    const payload = {
      appointment: {
        name: bookingData.name.trim(),
        phone: phone,
        email: bookingData.email.trim(),
        city: bookingData.city,
        branch_id: bookingData.branchId || (initialBranches[0]?.id ? String(initialBranches[0].id) : null),
        date: bookingData.date,
        time: bookingData.time,
        notes: bookingData.notes,
        service_ids: selectedServiceIds
      }
    };

    try {
      const response = await sendRequest(
        "POST",
        `${BASE_URL}/v1/public_site/appointments`,
        payload
      );
      const successMessage = response?.data?.message || "Appointment booked successfully.";
      setBookingMessage({ type: "success", text: successMessage });
      
      setTimeout(() => {
        closeBookingForm();
      }, 1500);
    } catch (error) {
      const serverErrors = error?.data?.errors;
      if (serverErrors && Array.isArray(serverErrors) && serverErrors.length > 0) {
        setBookingMessage({ type: "error", text: serverErrors.join(", ") });
      } else {
        const errorMessage = error?.data?.message || error?.message || "Failed to book appointment. Please try again.";
        setBookingMessage({ type: "error", text: errorMessage });
      }
    } finally {
      setBookingLoading(false);
    }
  }, [bookingData, selectedServiceIds, initialBranches, closeBookingForm]);

  return (
    <div className="home-page pet-home">
      {carouselHtml.length > 0 && (
        <div className="carousel-wrapper pet-carousel-wrapper">
          <Slider {...settings}>
            {carouselHtml.map((image, index) => (
              <div key={`pet-carousel-${index}`} className="carousel-slide">
                <img
                  src={image}
                  alt={`Slide ${index + 1}`}
                  className="carousel-image"
                />
              </div>
            ))}
          </Slider>
        </div>
      )}

      {tagline && (
        <section
          className="pet-hero-content"
          dangerouslySetInnerHTML={{ __html: tagline }}
        />
      )}

      <section className="pet-products-section">
        <div className="container">
          <div className="pet-products-header">
            <h2>Our Pet Products</h2>
            <p>Curated products powered by your {companyName} catalog.</p>
          </div>

          {/* Category Filter */}
          {!productsLoading && !categoriesLoading && productCategories.length > 0 && (
            <div className={`pet-categories-filter ${productCategories.length + 1 > 5 ? 'pet-categories-slider' : ''} ${productCategories.length > 4 ? 'pet-categories-scrollable' : ''}`}>
              <button
                className={`pet-category-btn ${activeProductCategory === null ? "active" : ""}`}
                onClick={() => handleCategoryChange(null)}
              >
                All Products
              </button>
              {productCategories.slice(0, 4).map((category) => {
                const categoryId = String(category.id);
                const isActive = activeProductCategory === categoryId;
                return (
                  <button
                    key={categoryId}
                    className={`pet-category-btn ${isActive ? "active" : ""}`}
                    onClick={() => handleCategoryChange(categoryId)}
                  >
                    {capitalizeCategoryName(category?.name || "Category")}
                    {typeof category?.items_count === "number" && (
                      <span className="pet-category-count">{category.items_count}</span>
                    )}
                  </button>
                );
              })}
              {productCategories.length > 4 && (
                <button
                  className="pet-category-btn pet-more-category-btn"
                  onClick={openCategoryPopup}
                >
                  More Categories
                  <span className="pet-category-count">{productCategories.length - 4}+</span>
                </button>
              )}
            </div>
          )}

          {categoriesLoading && (
            <div className="pet-category-loading">
              <span className="shimmer" />
              <span className="shimmer" />
              <span className="shimmer" />
            </div>
          )}

          {categoriesError && (
            <div className="pet-error">{categoriesError}</div>
          )}

          {productsError && (
            <div className="pet-error">
              {productsError}
            </div>
          )}

          {productsLoading ? (
            <div className="loading-state">
              <div className="spinner" />
              <p>Loading pet products...</p>
            </div>
          ) : petProducts.length > 0 ? (
            petProducts.length > 4 ? (
              <div className="pet-products-slider-container">
                <button
                  type="button"
                  className="pet-slider-btn pet-slider-prev"
                  onClick={() => sliderRef.current?.slickPrev()}
                  aria-label="Previous products"
                >
                  <i className="fas fa-chevron-left"></i>
                </button>
                <Slider
                  ref={sliderRef}
                  dots={false}
                  infinite={false}
                  speed={500}
                  slidesToShow={Math.min(4, petProducts.length)}
                  slidesToScroll={1}
                  arrows={false}
                  swipeToSlide={true}
                  draggable={true}
                  responsive={[
                    {
                      breakpoint: 1200,
                      settings: {
                        slidesToShow: Math.min(3, petProducts.length),
                        slidesToScroll: 1,
                      },
                    },
                    {
                      breakpoint: 992,
                      settings: {
                        slidesToShow: Math.min(3, petProducts.length),
                        slidesToScroll: 1,
                      },
                    },
                    {
                      breakpoint: 768,
                      settings: {
                        slidesToShow: 2,
                        slidesToScroll: 1,
                      },
                    },
                    {
                      breakpoint: 480,
                      settings: {
                        slidesToShow: 1,
                        slidesToScroll: 1,
                      },
                    },
                  ]}
                  className="pet-products-slider"
                >
                  {petProducts.map((product) => {
                    const finalPrice = product.sales_price - (product.sales_discount || 0);
                    const originalPrice = product.sales_discount ? product.sales_price : product.mrp;
                    const discount = product.sales_discount || (product.mrp > finalPrice ? Math.round(((product.mrp - finalPrice) / product.mrp) * 100) : 0);

                    return (
                      <div key={product.id} className="pet-product-slide">
                        <div className="pet-product-card">
                          <div
                            className="pet-product-media"
                            onClick={() => navigate(`/products/${product.id}`)}
                          >
                            <img src={product.image} alt={product.item_name} />
                            {discount > 0 && (
                              <span className="pet-product-badge">{discount}% OFF</span>
                            )}
                            {product.quantity === -1 && (
                              <span className="pet-service-badge">Service</span>
                            )}
                          </div>
                          <div className="pet-card-body">
                            <div className="pet-card-meta">
                              <span className="pet-product-category">{capitalizeCategoryName(product.category)}</span>
                              {product.quantity > 0 && (
                                <span className="pet-product-stock">In Stock</span>
                              )}
                            </div>
                            <h3 className="pet-product-name">{product.item_name}</h3>
                            {product.description && (
                              <p className="pet-product-desc">{product.description}</p>
                            )}
                            <div className="pet-product-meta">
                              <div className="pet-product-pricing">
                                <span className="pet-price-final">{formatCurrency(finalPrice)}</span>
                                {originalPrice > finalPrice && (
                                  <span className="pet-price-original">{formatCurrency(originalPrice)}</span>
                                )}
                              </div>
                            </div>
                            <div className="pet-product-actions">
                              {product.quantity === -1 ? (
                                <button
                                  className="pet-btn primary"
                                  onClick={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    openBookingForm(product.id);
                                  }}
                                >
                                  <i className="fas fa-calendar-check"></i> Book Service
                                </button>
                              ) : (
                                <button
                                  className="pet-btn primary"
                                  onClick={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    handleAddToCart(product);
                                  }}
                                >
                                  <i className="fas fa-shopping-cart"></i> Add to Cart
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </Slider>
                <button
                  type="button"
                  className="pet-slider-btn pet-slider-next"
                  onClick={() => sliderRef.current?.slickNext()}
                  aria-label="Next products"
                >
                  <i className="fas fa-chevron-right"></i>
                </button>
              </div>
            ) : (
              <div className="pet-products-grid">
                {petProducts.map((product) => {
                  const finalPrice = product.sales_price - (product.sales_discount || 0);
                  const originalPrice = product.sales_discount ? product.sales_price : product.mrp;
                  const discount = product.sales_discount || (product.mrp > finalPrice ? Math.round(((product.mrp - finalPrice) / product.mrp) * 100) : 0);

                  return (
                    <div key={product.id} className="pet-product-card">
                      <div
                        className="pet-product-media"
                        onClick={() => navigate(`/products/${product.id}`)}
                      >
                        <img src={product.image} alt={product.item_name} />
                        {discount > 0 && (
                          <span className="pet-product-badge">{discount}% OFF</span>
                        )}
                        {product.quantity === -1 && (
                          <span className="pet-service-badge">Service</span>
                        )}
                      </div>
                      <div className="pet-card-body">
                        <div className="pet-card-meta">
                          <span className="pet-product-category">{capitalizeCategoryName(product.category)}</span>
                          {product.quantity > 0 && (
                            <span className="pet-product-stock">In Stock</span>
                          )}
                        </div>
                        <h3 className="pet-product-name">{product.item_name}</h3>
                        {product.description && (
                          <p className="pet-product-desc">{product.description}</p>
                        )}
                        <div className="pet-product-meta">
                          <div className="pet-product-pricing">
                            <span className="pet-price-final">{formatCurrency(finalPrice)}</span>
                            {originalPrice > finalPrice && (
                              <span className="pet-price-original">{formatCurrency(originalPrice)}</span>
                            )}
                          </div>
                        </div>
                        <div className="pet-product-actions">
                          {product.quantity === -1 ? (
                            <button
                              className="pet-btn primary"
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                openBookingForm(product.id);
                              }}
                            >
                              <i className="fas fa-calendar-check"></i> Book Service
                            </button>
                          ) : (
                            <button
                              className="pet-btn primary"
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                handleAddToCart(product);
                              }}
                            >
                              <i className="fas fa-shopping-cart"></i> Add to Cart
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          ) : (
            <div className="no-products">
              <p>
                {activeCategoryLabel
                  ? `No products found in "${activeCategoryLabel}" category.`
                  : "No pet products available right now."}
              </p>
            </div>
          )}
        </div>
      </section>

      {bodyHtml && (
        <section
          className="pet-body-content"
          ref={bodyContentRef}
          dangerouslySetInnerHTML={{ __html: bodyHtml }}
        />
      )}

      {/* Category Popup Modal */}
      {categoryPopupOpen && (
        <div className="pet-category-popup-overlay" onClick={closeCategoryPopup}>
          <div className="pet-category-popup-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pet-category-popup-header">
              <h3>All Categories</h3>
              <button type="button" className="pet-category-popup-close" onClick={closeCategoryPopup}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="pet-category-popup-content">
              <button
                className={`pet-category-popup-item ${activeProductCategory === null ? "active" : ""}`}
                onClick={() => handleCategoryChange(null)}
              >
                <span className="pet-category-popup-name">All Products</span>
                <i className="fas fa-chevron-right"></i>
              </button>
              {productCategories.map((category) => {
                const categoryId = String(category.id);
                const isActive = activeProductCategory === categoryId;
                return (
                  <button
                    key={categoryId}
                    className={`pet-category-popup-item ${isActive ? "active" : ""}`}
                    onClick={() => handleCategoryChange(categoryId)}
                  >
                    <span className="pet-category-popup-name">
                      {capitalizeCategoryName(category?.name || "Category")}
                    </span>
                    <div className="pet-category-popup-meta">
                      {typeof category?.items_count === "number" && (
                        <span className="pet-category-popup-count">{category.items_count} items</span>
                      )}
                      <i className="fas fa-chevron-right"></i>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Booking Form Modal */}
      {bookingFormOpen && (
        <div className="pet-booking-overlay" onClick={closeBookingForm}>
          <div className="pet-booking-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pet-booking-header">
              <div>
                <h3>Book Pet Service</h3>
                <p className="pet-booking-subtitle">Our team will confirm your appointment shortly.</p>
              </div>
              <button type="button" className="pet-booking-close" onClick={closeBookingForm}>
                ×
              </button>
            </div>

            <form className="pet-booking-form" onSubmit={handleBookingSubmit}>
              <div className="pet-booking-grid">
                <div>
                  <label htmlFor="bookingName">Full Name *</label>
                  <input
                    id="bookingName"
                    name="name"
                    type="text"
                    placeholder="Enter your name"
                    value={bookingData.name}
                    onChange={handleBookingFieldChange}
                    required
                  />
                </div>
                <div>
                  <label htmlFor="bookingPhone">Phone Number *</label>
                  <input
                    id="bookingPhone"
                    name="phone"
                    type="tel"
                    placeholder="10-digit mobile (6,7,8,9)"
                    maxLength="10"
                    value={bookingData.phone}
                    onChange={handlePhoneInput}
                    required
                  />
                </div>
                <div>
                  <label htmlFor="bookingEmail">Email</label>
                  <input
                    id="bookingEmail"
                    name="email"
                    type="email"
                    placeholder="you@email.com"
                    value={bookingData.email}
                    onChange={handleBookingFieldChange}
                  />
                </div>
                <div>
                  <label htmlFor="bookingBranch">Branch Location *</label>
                  <select
                    id="bookingBranch"
                    name="branchId"
                    value={bookingData.branchId}
                    onChange={handleBookingFieldChange}
                    required
                  >
                    <option value="">Select branch</option>
                    {initialBranches.map((branch) => (
                      <option key={branch.id} value={branch.id}>
                        {branch.name} {branch.city ? `• ${branch.city}` : ""}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="bookingCity">City</label>
                  <input
                    id="bookingCity"
                    name="city"
                    type="text"
                    placeholder="Preferred city"
                    value={bookingData.city}
                    onChange={handleBookingFieldChange}
                  />
                </div>
                <div>
                  <label htmlFor="bookingDate">Appointment Date *</label>
                  <input
                    id="bookingDate"
                    name="date"
                    type="date"
                    min={todayDate}
                    value={bookingData.date}
                    onChange={handleBookingFieldChange}
                    required
                  />
                </div>
                <div>
                  <label htmlFor="bookingTime">Preferred Time *</label>
                  <input
                    id="bookingTime"
                    name="time"
                    type="time"
                    value={bookingData.time}
                    onChange={handleBookingFieldChange}
                    required
                  />
                </div>
              </div>

              <div className="pet-booking-services">
                <label>Select Services *</label>
                {petServices.length > 0 ? (
                  <div className="pet-booking-service-grid">
                    {petServices.map((service) => {
                      const isSelected = selectedServiceIds.includes(Number(service.id));
                      return (
                        <button
                          key={service.id}
                          type="button"
                          className={`pet-chip ${isSelected ? "selected" : ""}`}
                          onClick={() => toggleServiceSelection(service.id)}
                        >
                          <span className="pet-chip-name">{service.item_name}</span>
                          <span className="pet-chip-price">{formatCurrency(service.sales_price)}</span>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <p className="pet-booking-empty">Pet services are loading...</p>
                )}
              </div>

              <div>
                <label htmlFor="bookingNotes">Special Requests</label>
                <textarea
                  id="bookingNotes"
                  name="notes"
                  rows="3"
                  placeholder="Share any special requirements or notes..."
                  value={bookingData.notes}
                  onChange={handleBookingFieldChange}
                />
              </div>

              {bookingMessage && (
                <div className={`pet-booking-message ${bookingMessage.type}`}>
                  {bookingMessage.text}
                </div>
              )}

              <div className="pet-booking-actions">
                <button type="submit" disabled={bookingLoading}>
                  {bookingLoading ? "Booking..." : "Confirm Appointment"}
                </button>
                <button type="button" className="pet-booking-cancel" onClick={closeBookingForm}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PetHome;

